import { useCallback, useMemo, useState } from 'react';
import { Discount } from 'shared/types/cart/Discount';
import useSWR, { mutate } from 'swr';
import mapCosts from 'helpers/utils/mapCosts';
import { sdk } from 'sdk';
import { Cart, Store, DeliveryOption, TaxRateData, ShippingMethod, StoreOpeningTime } from 'shared/types/cart';
import { Order } from 'shared/types/cart/Order';
import { Variant } from 'shared/types/product';
import { revalidateOptions } from 'frontastic';
import { CartDetails, UseCartReturn } from './types';
import { PreferenceData } from 'shared/types/cart/Order';
import { format } from 'date-fns';
import useGivex from '../useGivex';
import {
  GetEstimatedShippingPricePayload,
  GetTaxRatePayload,
  GiftcardRequestPayload,
  RemoveGiftcardPayload,
} from 'sdk/composable-commerce/types/payloads/CartPayloads';
import useLocale from 'helpers/hooks/useLocale';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';

const useCart = (): UseCartReturn => {
  const extensions = sdk.composableCommerce;
  const { getCountry, getCurrency, ctLocale: locale } = useLocale();
  const givex = useGivex();

  const result = useSWR('/action/cart/getCart', extensions.cart.getCart, revalidateOptions);
  const freezeCart = useCallback(async (cartId: string): Promise<Cart> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.freezeCart({ cartId });

    mutate('/action/cart/getCart', res);

    return (res.isError ? {} : res.data) as Cart;
  }, []);
  const unfreezeCart = useCallback(async (cartId: string): Promise<Cart> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.unfreezeCart({ cartId });

    mutate('/action/cart/getCart', res);

    return (res.isError ? {} : res.data) as Cart;
  }, []);
  const shippingMethodsResults = useSWR(
    '/action/cart/getShippingMethods',
    extensions.cart.getShippingMethods,
    revalidateOptions,
  );

  const taxCategoryRateResult = useSWR(
    '/action/cart/getTaxCategoryRate',
    () =>
      sdk.callAction({
        actionName: '/cart/getTaxCategoryRate',
        payload: { taxCategory: 'standard', country: getCountry() },
      }),
    revalidateOptions,
  );

  const estimatedShippingPriceResult = useSWR(
    '/action/cart/getEstimatedShippingPrice',
    () =>
      sdk.callAction({
        actionName: '/cart/getEstimatedShippingPrice',
        payload: { country: getCountry() },
      }),
    revalidateOptions,
  );

  const data = result.data?.isError ? {} : { data: result.data?.data as unknown as Cart };

  // Get all product SKUs of non-perpetual products only
  const productSKUs = useMemo(() => {
    return data?.data?.lineItems?.filter((item) => !item?.isPerpetualStock)?.map((item) => item?.variant?.sku) || [];
  }, [data.data]);

  const shippingMethods = shippingMethodsResults.data?.isError ? {} : { data: shippingMethodsResults.data?.data };

  const totalItems = (data.data as Cart)?.lineItems?.reduce((acc, curr) => acc + (curr.count as number), 0) ?? 0;

  const isEmpty = !data?.data?.lineItems?.length;

  const hasShippingInfo = data?.data?.shippingInfo;

  const isShippingAccurate = !!data?.data?.shippingInfo;

  const hasOutOfStockItems = !!data?.data?.lineItems?.some((lineItem) => !lineItem.variant?.isOnStock);

  const transaction = useMemo(
    () => mapCosts({ reference: 'cart', cart: data.data, currency: getCurrency() }),
    [data.data, getCurrency()],
  );

  const hasExcludedItem = !!data?.data?.lineItems?.some((item) => {
    const country = data?.data?.shippingAddress?.country;
    const site = country ? (country === 'GB' ? 'UK' : 'EU') : getCountry() === 'GB' ? 'UK' : 'EU';

    if (!item.exclusions) {
      return false;
    }

    if (item.exclusions && item.exclusions.length > 0) {
      return item.exclusions.some((exclusion) => exclusion === site);
    }
    return false;
  });

  const hasItemsBelowMinQty = data?.data?.lineItems?.some((lineItem) => {
    const minQty = lineItem.variant?.attributes?.minQty || 0;
    return (lineItem.count as number) < minQty;
  });

  const hasOnlineGiftcard =
    data?.data?.lineItems?.some((item) => item.isGiftcard === true && item.giftcardAttrs?.type === 'ONLINE') || false;

  const hasCustomFitItem = data?.data?.lineItems?.some((item) => item.isCustomFit === true) || false;

  const taxCategoryRate = taxCategoryRateResult.data?.isError ? {} : { data: taxCategoryRateResult.data?.data };

  const hasPersonalisedItem = data?.data?.lineItems?.some((item) => item.isPersonalized === true) || false;

  const allItemsPersonalized = data?.data?.lineItems?.every((item) => item.isPersonalized === true) || false;

  const estimatedShippingPrice = estimatedShippingPriceResult.data?.isError
    ? {}
    : { data: estimatedShippingPriceResult.data?.data };

  const addItem = useCallback(
    async (variant: Variant, quantity: number, customFitOptions?: string[], customFitPrice?: number) => {
      const extensions = sdk.composableCommerce;
      const payload = {
        variant: {
          // sku: variant?.sku,
          count: quantity,
          amount: variant?.amount,
          type: variant?.type,
          recipient: variant?.recipient,
          recipientemail: variant?.recipientemail,
          cid: variant?.id,
          customPrice: variant?.customPrice,
          pid: variant?.sku,
          ...variant,
        },
      };
      console.log('useCart======', payload);
      try {
        const res = await extensions.cart.addItem(payload);
        await mutate('/action/cart/getCart', res);
        if (!hasShippingInfo) {
          mutate('/action/cart/getEstimatedShippingPrice');
        }
        return res;
      } catch (error) {
        return error;
      }
    },
    [],
  );

  const checkout = useCallback(async (session: string) => {
    const res = await sdk.callAction({
      actionName: 'cart/checkoutCart',
      payload: { session: session },
    });
    mutate('/action/cart/getCart');

    return (res.isError ? {} : res.data) as Order;
  }, []);

  const getOrder = useCallback(async (orderId: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.queryOrders({ orderNumbers: [orderId] });
    mutate('/action/cart/getCart');

    return (res.isError ? {} : res.data.items?.[0]) as Order;
  }, []);

  const getCheckoutOrder = useCallback(async (orderId: string) => {
    const res = await sdk.callAction({ actionName: 'cart/getCheckoutOrder', query: { orderId: orderId } });
    mutate('/action/cart/getCheckoutOrder');

    return (res.isError ? {} : res.data) as Order;
  }, []);

  const updateOrder = useCallback(async (orderNumber: string, marketingPreference: PreferenceData) => {
    const res = await sdk.callAction({
      actionName: 'cart/updateOrderForMarketingPreference',
      payload: { orderNumber, marketingPreference },
    });
    mutate('/action/cart/updateOrderForMarketingPreference');

    return (res.isError ? {} : res.data) as Order;
  }, []);

  const orderHistory = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.queryOrders();

    return res.isError ? ([] as Order[]) : (res.data.items as Order[]);
  }, []);

  const getProjectSettings = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.project.getSettings();

    return res.isError ? {} : res.data;
  }, []);

  const removeItem = useCallback(async (lineItemId: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      lineItem: { id: lineItemId },
    };
    const cartRes = await extensions.cart.getCart();
    const currentUrl = document.location.origin;
    const res = await extensions.cart.removeItem(payload);
    if (res.isError === false && cartRes.isError === false) {
      cartRes.data.lineItems?.forEach((item) => {
        if (item.lineItemId === lineItemId) {
          var basketItems = [
            {
              id: item.productId || '',
              productId: item.productKey || '',
              name: item.name || '',
              price: item.price?.centAmount != undefined ? (item.price.centAmount / 100).toFixed(2) : 0.0,
              quantity: item.count || '',
              brand: item?.variant?.attributes?.brand || '',
              sku: item.variant?.sku || '',
              category: item.categoryName || '',
            },
          ];
          //trigger ContentPush
          const dataLayerContentPush = new DataLayerHelpers(currentUrl, locale, res.data);
          dataLayerContentPush.pushCartContentToDataLayer();
          const dataLayer = new DataLayerHelpers(currentUrl, locale, basketItems[0]);
          dataLayer.setRemoveCartItemDataLayer();
        }
      });
    }
    await mutate('/action/cart/getCart', res);
    if (!hasShippingInfo) {
      mutate('/action/cart/getEstimatedShippingPrice');
    }
  }, []);

  const updateItem = useCallback(async (lineItemId: string, newQuantity: number): Promise<Cart> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      lineItem: {
        id: lineItemId,
        count: newQuantity,
      },
    };
    const res = await extensions.cart.updateItem(payload);
    await mutate('/action/cart/getCart', res);
    if (!hasShippingInfo) {
      mutate('/action/cart/getEstimatedShippingPrice');
    }
    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const updateCart = useCallback(async (payload: CartDetails): Promise<Cart> => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.updateCart(payload);

    await mutate('/action/cart/getCart', res);
    if (!hasShippingInfo) {
      mutate('/action/cart/getEstimatedShippingPrice');
    }

    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const setShippingMethod = useCallback(
    async (shippingMethodId: string, key: string, deliveryDate: Date): Promise<Cart> => {
      const extensions = sdk.composableCommerce;

      const formattedDate = format(deliveryDate, 'yyyy-MM-dd');
      const payload = {
        shippingMethod: {
          id: shippingMethodId,
          key: key,
          date: new Date(formattedDate),
        },
      };

      const res = await extensions.cart.setShippingMethod(payload);

      mutate('/action/cart/getCart', res);

      return (res.isError ? {} : res.data) as Cart;
    },
    [],
  );

  const redeemDiscountCode = useCallback(async (code: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      code: code,
    };
    const res = await extensions.cart.redeemDiscountCode(payload);

    if (!res.isError && (res.data as Cart).cartId) {
      await mutate('/action/cart/getCart', res);
      if (!hasShippingInfo) {
        mutate('/action/cart/getEstimatedShippingPrice');
      }
    } else {
      throw new Error('code not valid');
    }
  }, []);

  const removeDiscountCode = useCallback(async (discount: Discount) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.removeDiscountCode({ discountId: discount.discountId as string });

    await mutate('/action/cart/getCart', res);
    if (!hasShippingInfo) {
      mutate('/action/cart/getEstimatedShippingPrice');
    }
  }, []);

  const applyGiftcard = useCallback(
    async (giftcardNumber: string, maxAmount?: number) => {
      const res = await givex.applyGiftcard({
        giftcardNumber: giftcardNumber,
        maxAmount: maxAmount,
      } as GiftcardRequestPayload);

      if (res.isError) {
        let errorMessage = res.error?.message;
        if (errorMessage?.indexOf('Error: ') !== -1) {
          errorMessage = errorMessage.substring(7);
        }
        throw new Error(errorMessage);
      } else {
        mutate('/action/cart/getCart', res.data);
      }
    },
    [givex.applyGiftcard], //eslint-disable-line
  );

  const removeGiftcard = useCallback(async (giftcard: string) => {
    const extensions = sdk.composableCommerce;
    const payload = { giftcard } as RemoveGiftcardPayload;
    const res = await extensions.cart.removeGiftcard(payload);
    if (res.isError) {
      throw new Error('Remove Giftcard Failed');
    }
    mutate('/action/cart/getCart', res);
  }, []);

  const getTaxCategoryRate = useCallback(async (taxCategory: string, country: string): Promise<TaxRateData> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getTaxCategoryRate({ taxCategory, country } as GetTaxRatePayload);
    if (!res.isError) {
      return res.data as TaxRateData;
    } else {
      throw new Error('taxCategory not valid');
    }
  }, []);

  const getEstimatedShippingPrice = useCallback(async (country: string): Promise<ShippingMethod> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getEstimatedShippingPrice({
      country,
    } as GetEstimatedShippingPricePayload);
    if (!res.isError) {
      return res.data as ShippingMethod;
    } else {
      throw new Error('taxCategory not valid');
    }
  }, []);

  const getStores = useCallback(async (): Promise<Store[]> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getStores();

    return (res.isError ? {} : res.data) as Store[];
  }, []);

  const getStoreOpeningTime = useCallback(async (googlePlaceID: string): Promise<StoreOpeningTime> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getStoreOpeningTime({ googlePlaceID });

    return (res.isError ? {} : res.data) as StoreOpeningTime;
  }, []);

  const getAvailableShippingMethods = useCallback(async (): Promise<ShippingMethod[]> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getAvailableShippingMethods();

    if (!res.isError) {
      return res.data as ShippingMethod[];
    }

    return [];
  }, []);

  const getAvailableDeliveryOptions = useCallback(async (selectedCountryCode: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getAvailableDeliveryOptions({ selectedCountryCode });

    if (!res.isError) {
      return res.data as DeliveryOption[];
    }
    return [];
  }, []);

  const setDeliveryOption = useCallback(async (deliveryOption: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.setDeliveryOption({ deliveryOption });
    if (!res.isError && (res.data as Cart).cartId) {
      await mutate('/action/cart/getCart', res);
      if (!hasShippingInfo) {
        mutate('/action/cart/getEstimatedShippingPrice');
      }
    } else {
      throw new Error('code not valid');
    }
    return;
  }, []);

  const setDeliveryInstruction = useCallback(async (deliveryInstruction: string): Promise<void> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      deliveryInstruction: deliveryInstruction,
    };

    const res = await extensions.cart.setDeliveryInstruction(payload);

    mutate('/action/cart/getCart', res);
  }, []);

  const getOrderByCartId = useCallback(async (cartId: string) => {
    const payload = {
      cartId,
    };
    const response = await sdk.callAction({
      actionName: 'cart/getOrderByCartId',
      payload: payload,
    });

    return response.isError ? {} : (response.data as Order);
  }, []);

  const cartRecalculate = useCallback(async (): Promise<Cart> => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.cart.cartRecalculate();
    await mutate('/action/cart/getCart', res);
    if (!hasShippingInfo) {
      mutate('/action/cart/getEstimatedShippingPrice');
    }
    return (res.isError ? {} : res.data) as Cart;
  }, []);

  return {
    ...data,
    totalItems,
    isEmpty,
    isShippingAccurate,
    hasOutOfStockItems,
    transaction,
    addItem,
    updateCart,
    setShippingMethod,
    removeItem,
    updateItem,
    shippingMethods,
    checkout,
    getOrder,
    getOrderByCartId,
    getCheckoutOrder,
    // orderHistory,
    getProjectSettings,
    redeemDiscountCode,
    removeDiscountCode,
    applyGiftcard,
    removeGiftcard,
    getTaxCategoryRate,
    getEstimatedShippingPrice,
    getStores,
    getAvailableDeliveryOptions,
    setDeliveryOption,
    taxCategoryRate,
    estimatedShippingPrice,
    getStoreOpeningTime,
    productSKUs,
    setDeliveryInstruction,
    cartRecalculate,
    getAvailableShippingMethods,
    updateOrder,
    hasExcludedItem,
    hasOnlineGiftcard,
    hasCustomFitItem,
    hasPersonalisedItem,
    allItemsPersonalized,
    hasItemsBelowMinQty,
    freezeCart,
    unfreezeCart,
  };
};

export default useCart;
